import { normalizeGoidType } from '@/api/types'
import { MRDMultiSelectEditor } from '@/components/Grid/CellEditors'
import { MRDCell } from '@/components/Grid/CellRenderers'
import { brandedColumnDef } from '@/utils/branded'
import { BBDTag } from '@gravitate-js/excalibrr'
import {
  ColDef,
  FilterValueGetterParams,
  ICellRendererParams,
  ValueGetterParams,
} from 'ag-grid-community'
import { EntityObject } from '../types'

/**
 * Get base column definitions for all entity types
 */
const getBaseColumnDefs = (): ColDef<EntityObject>[] => [
  {
    headerName: 'GOID',
    field: 'goid',
    width: 120,
    sortable: true,
    filter: true,
  },
  {
    headerName: 'Name',
    field: 'name',
    flex: 1,
    sortable: true,
    filter: true,
  },
]

/**
 * Get entity-specific column definitions
 */
const getEntitySpecificColumns = (
  entityType: string,
): ColDef<EntityObject>[] => {
  const baseColumns = getBaseColumnDefs()

  switch (entityType) {
    case 'company':
    case 'counterparty':
      return [
        ...baseColumns,
        {
          headerName: 'Type',
          field: 'type',
          width: 150,
          sortable: true,
          filter: true,
        },
        {
          headerName: 'Status',
          field: 'status',
          width: 120,
          sortable: true,
          filter: true,
        },
      ]

    case 'location':
      return [
        ...baseColumns,
        {
          headerName: 'City',
          field: 'city',
          width: 150,
          sortable: true,
          filter: true,
        },
        {
          headerName: 'State',
          field: 'state',
          width: 100,
          sortable: true,
          filter: true,
        },
        {
          headerName: 'Country',
          field: 'country',
          width: 100,
          sortable: true,
          filter: true,
        },
      ]

    case 'product':
      return [
        ...baseColumns,
        {
          headerName: 'Product Code',
          field: 'product_code',
          width: 150,
          sortable: true,
          filter: true,
        },
        {
          headerName: 'Description',
          field: 'description',
          flex: 1,
          sortable: true,
          filter: true,
        },
      ]

    case 'terminal':
    case 'site':
      return [
        ...baseColumns,
        {
          headerName: 'Location',
          field: 'location_name',
          width: 200,
          sortable: true,
          filter: true,
        },
        {
          headerName: 'Owner',
          field: 'owner_name',
          width: 200,
          sortable: true,
          filter: true,
        },
      ]

    case 'tank':
      return [
        ...baseColumns,
        {
          headerName: 'Site',
          field: 'site_name',
          width: 200,
          sortable: true,
          filter: true,
        },
        {
          headerName: 'Capacity',
          field: 'capacity',
          width: 120,
          sortable: true,
          filter: true,
        },
        {
          headerName: 'Product',
          field: 'product_name',
          width: 150,
          sortable: true,
          filter: true,
        },
      ]

    default:
      return baseColumns
  }
}

/**
 * Process column definitions from grid data
 */
const processGridColumnDefs = (gridData: unknown): ColDef<EntityObject>[] => {
  const gd = gridData as Record<string, unknown> | undefined
  const defs = gd?.column_defs as unknown
  if (!Array.isArray(defs)) return []

  return (defs as Record<string, unknown>[]).map((col) => {
    // Add editable to all columns except certain system fields
    const fieldName = String((col as Record<string, unknown>).field || '')
    const isSystemField = ['id', 'goid', 'revision_id'].includes(fieldName)
    const baseCol: ColDef<EntityObject> = {
      ...(col as ColDef<EntityObject>),
      editable: !isSystemField,
    }

    if (fieldName === 'branded') {
      return { ...baseCol, ...brandedColumnDef }
    }

    // Handle location and geofence objects to display as 'lat, lon'
    if (fieldName === 'location' || fieldName === 'geofence') {
      return {
        ...baseCol,
        cellRenderer: (params: ICellRendererParams<EntityObject>) => {
          const location = params.value
          if (
            location &&
            typeof location === 'object' &&
            location.lat !== undefined &&
            location.lon !== undefined
          ) {
            // Format with proper precision - show 6 decimal places for coordinates
            return `${location.lat.toFixed(6)}, ${location.lon.toFixed(6)}`
          }
          return location || 'N/A'
        },
        editable: false, // Location objects shouldn't be directly editable
        valueGetter: (params: ValueGetterParams<EntityObject>) => {
          const location = params.data[fieldName as keyof EntityObject]
          if (
            location &&
            typeof location === 'object' &&
            location.lat !== undefined &&
            location.lon !== undefined
          ) {
            return `${location.lat.toFixed(6)}, ${location.lon.toFixed(6)}`
          }
          return location || 'N/A'
        },
        filterValueGetter: (params: FilterValueGetterParams<EntityObject>) => {
          const location = params.data[fieldName as keyof EntityObject]
          if (
            location &&
            typeof location === 'object' &&
            location.lat !== undefined &&
            location.lon !== undefined
          ) {
            return `${location.lat.toFixed(6)}, ${location.lon.toFixed(6)}`
          }
          return location || 'N/A'
        },
      }
    }

    // Convert boolean columns to Yes/No display with dropdown editor
    if ((col as Record<string, unknown>).type === 'boolean') {
      return {
        ...baseCol,
        editable: fieldName !== 'is_active',
        cellRenderer: (params: ICellRendererParams<EntityObject>) => {
          const value = params.data?.[fieldName as keyof EntityObject]
          if (value === true) {
            return (
              <BBDTag color='success' style={{ width: 'min-content' }}>
                Yes
              </BBDTag>
            )
          }
          return (
            <BBDTag color='default' style={{ width: 'min-content' }}>
              No
            </BBDTag>
          )
        },
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: {
          values: [true, false],
        },
        valueFormatter: (params) => (params.value === true ? 'Yes' : 'No'),
      }
    }

    // Handle GOID reference columns (detected via goid_type from backend)
    const goidType = (col as Record<string, unknown>).goid_type as
      | string
      | undefined
    const isMulti = (col as Record<string, unknown>).multi === true

    if (goidType) {
      const normalizedType = normalizeGoidType(goidType)
      return {
        ...baseCol,
        editable: true,
        cellRenderer: (params: ICellRendererParams<EntityObject>) => {
          const value = params.data?.[fieldName as keyof EntityObject]
          // Normalize to array for display
          const goids = isMulti
            ? Array.isArray(value)
              ? (value as string[])
              : value
                ? [value as string]
                : null
            : value
              ? [value as string]
              : null
          return <MRDCell goids={goids} />
        },
        cellEditor: MRDMultiSelectEditor,
        cellEditorParams: {
          type: normalizedType,
          multi: isMulti,
        },
        valueGetter: (params: ValueGetterParams<EntityObject>) => {
          const value = params.data?.[fieldName as keyof EntityObject]
          if (isMulti) {
            return Array.isArray(value) ? value : value ? [value] : []
          }
          // Single value - wrap in array for editor
          return value ? [value] : []
        },
        valueSetter: (params) => {
          // Editor now returns correct type (string for single, array for multi)
          params.data[fieldName as keyof EntityObject] =
            params.newValue as never
          return true
        },
      }
    }

    return baseCol
  })
}

/**
 * Get complete column definitions for entity type
 */
export const getColumnDefinitions = (
  entityType: string,
  gridData?: unknown,
): ColDef<EntityObject>[] => {
  // Use column_defs from grid endpoint response when available
  if (gridData?.column_defs) {
    return processGridColumnDefs(gridData)
  }

  return getEntitySpecificColumns(entityType)
}
